import { createElement, type ComponentType, type CSSProperties } from 'react';
import {
  ArrowCounterClockwise,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowSquareOut,
  ArrowUp,
  ArrowsClockwise,
  ArrowsDownUp,
  Bell,
  BookOpen,
  Buildings,
  CalendarBlank,
  CaretDown,
  CaretLeft,
  CaretRight,
  CaretUp,
  ChartBar,
  ChatText,
  Check,
  CheckCircle,
  Clock,
  Code,
  Copy,
  CreditCard,
  Cursor,
  Database,
  DeviceMobile,
  DotsSixVertical,
  DotsThree,
  DownloadSimple,
  Envelope,
  Export,
  Eye,
  File,
  FloppyDisk,
  Folder,
  Funnel,
  Gear,
  Desktop,
  Globe,
  Info,
  Key,
  Laptop,
  Lightbulb,
  Lightning,
  LinkSimple,
  Lock,
  LockOpen,
  MagnifyingGlass,
  Megaphone,
  Microphone,
  Minus,
  Note,
  Pause,
  PencilSimple,
  Phone,
  Play,
  Paperclip,
  Plus,
  Question,
  ShareNetwork,
  ShieldCheck,
  SortAscending,
  SortDescending,
  Sparkle,
  SpeakerHigh,
  SquaresFour,
  Stack,
  Stop,
  Trash,
  TreeStructure,
  UploadSimple,
  User,
  UserPlus,
  Users,
  VideoCamera,
  Warning,
  X,
  XCircle,
} from '@phosphor-icons/react';

/** Ícones do app (Phosphor). A chave é o nome usado no código; o valor, o componente. */
export type IconComponent = ComponentType<{ size?: number | string; className?: string; style?: CSSProperties; 'aria-label'?: string }>;

export const ICONS = {
  Apps: SquaresFour,
  ArrowDown: ArrowDown,
  ArrowLeft: ArrowLeft,
  ArrowRight: ArrowRight,
  ArrowUp: ArrowUp,
  Bell: Bell,
  Building: Buildings,
  Bulb: Lightbulb,
  Camera: VideoCamera,
  CaretDown: CaretDown,
  CaretUp: CaretUp,
  Charts: ChartBar,
  Checkmark: Check,
  CheckmarkWithCircle: CheckCircle,
  ChevronDown: CaretDown,
  ChevronLeft: CaretLeft,
  ChevronRight: CaretRight,
  ChevronUp: CaretUp,
  Clock: Clock,
  Connect: LinkSimple,
  Copy: Copy,
  CreditCard: CreditCard,
  Database: Database,
  Diagram2: ShareNetwork,
  Download: DownloadSimple,
  Drag: DotsSixVertical,
  Edit: PencilSimple,
  Ellipsis: DotsThree,
  Email: Envelope,
  Export: Export,
  File: File,
  Import: DownloadSimple,
  InfoWithCircle: Info,
  InviteUser: UserPlus,
  Key: Key,
  LightningBolt: Lightning,
  Link: LinkSimple,
  Lock: Lock,
  MagnifyingGlass: MagnifyingGlass,
  Megaphone: Megaphone,
  Mic: Microphone,
  Minus: Minus,
  Mobile: DeviceMobile,
  MultiLayers: Stack,
  Note: Note,
  OpenNewTab: ArrowSquareOut,
  Pause: Pause,
  Person: User,
  PersonGroup: Users,
  Phone: Phone,
  Play: Play,
  Paperclip: Paperclip,
  Plus: Plus,
  QuestionMarkWithCircle: Question,
  Refresh: ArrowsClockwise,
  Save: FloppyDisk,
  Settings: Gear,
  Shield: ShieldCheck,
  SMS: ChatText,
  SortAscending: SortAscending,
  SortDescending: SortDescending,
  Sparkle: Sparkle,
  Trash: Trash,
  Undo: ArrowCounterClockwise,
  Unlock: LockOpen,
  Upload: UploadSimple,
  Visibility: Eye,
  Warning: Warning,
  X: X,
  XWithCircle: XCircle,
  AudioFull: SpeakerHigh,
  Calendar: CalendarBlank,
  Code: Code,
  Cursor: Cursor,
  Diagram3: TreeStructure,
  Filter: Funnel,
  Folder: Folder,
  GlobeAmericas: Globe,
  Desktop: Desktop,
  Laptop: Laptop,
  Read: BookOpen,
  Stop: Stop,
  Unsorted: ArrowsDownUp,
} as const satisfies Record<string, IconComponent>;

export type IconKey = keyof typeof ICONS;

/** Ícone por nome vindo de dado dinâmico (catálogo de planos, blocos do fluxo, canais). */
export function glyph(key: string, size = 16, className?: string) {
  const Icon: IconComponent = (ICONS as Record<string, IconComponent>)[key] ?? Info;
  return createElement(Icon, { size, className });
}

/** Ícone a partir de um componente de `ICONS` guardado em dado (ex.: `<Glyph icon={block.icon} size={20} />`). */
export function Glyph({ icon, size = 16, className, style }: { icon: IconComponent; size?: number; className?: string; style?: CSSProperties }) {
  return createElement(icon, { size, className, style } as never);
}
